import ContractorReferralApi from "../api/contractorReferral";
import { getFrontendAppBaseUrl } from "./referralTracking";

export const CONTRACTOR_VISITOR_ID_KEY = "solarent_contractor_visitor_id";
export const CONTRACTOR_REFERRAL_INFO_KEY = "solarent_contractor_referral_info";
export const CONTRACTOR_REFERRAL_SESSION_TRACK_PREFIX =
  "solarent_contractor_referral_tracked_";

function safeParseJson(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function buildContractorReferralLink(referralCode) {
  const code = (referralCode || "").trim();
  if (!code) return "";
  return `${getFrontendAppBaseUrl()}/c/${encodeURIComponent(code)}`;
}

export function getOrCreateContractorVisitorId() {
  if (typeof window === "undefined") return null;

  const existing = window.localStorage.getItem(CONTRACTOR_VISITOR_ID_KEY);
  if (existing) return existing;

  let visitorId;
  if (window.crypto?.randomUUID) {
    visitorId = window.crypto.randomUUID();
  } else {
    visitorId = `cv-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
  }

  window.localStorage.setItem(CONTRACTOR_VISITOR_ID_KEY, visitorId);
  return visitorId;
}

export function getStoredContractorReferral() {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(CONTRACTOR_REFERRAL_INFO_KEY);
  return raw ? safeParseJson(raw) : null;
}

export function storeContractorReferralFallback(referralCode) {
  const code = (referralCode || "").trim();
  if (!code || typeof window === "undefined") return;

  window.localStorage.setItem(
    CONTRACTOR_REFERRAL_INFO_KEY,
    JSON.stringify({
      referralCode: code,
      storedAt: new Date().toISOString(),
    })
  );
}

function wasTrackedThisSession(referralCode) {
  try {
    return (
      window.sessionStorage.getItem(
        `${CONTRACTOR_REFERRAL_SESSION_TRACK_PREFIX}${referralCode}`
      ) === "1"
    );
  } catch {
    return false;
  }
}

function markTrackedThisSession(referralCode) {
  try {
    window.sessionStorage.setItem(
      `${CONTRACTOR_REFERRAL_SESSION_TRACK_PREFIX}${referralCode}`,
      "1"
    );
  } catch {
    /* ignore */
  }
}

export function resolveContractorReferralCode({ routeCode, search } = {}) {
  const fromRoute = (routeCode || "").trim();
  if (fromRoute) return fromRoute;

  if (search) {
    const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
    const fromQuery = params.get("contractorRef") || params.get("cref");
    if (fromQuery) return fromQuery.trim();
  }

  const stored = getStoredContractorReferral();
  return stored?.referralCode || null;
}

export function getContractorReferralFieldsForSignup() {
  const contractorVisitorId = getOrCreateContractorVisitorId();
  const stored = getStoredContractorReferral();
  const contractorReferralCode = stored?.referralCode || null;

  const payload = { contractorVisitorId };
  if (contractorReferralCode) {
    payload.contractorReferralCode = contractorReferralCode;
  }
  return payload;
}

export function getContractorVisitorIdForRequest() {
  return getOrCreateContractorVisitorId();
}

export async function attachContractorReferralRegistrationIfNeeded() {
  const visitorId = getOrCreateContractorVisitorId();
  const stored = getStoredContractorReferral();
  const referralCode = stored?.referralCode;

  if (!visitorId) return;

  try {
    // Ensure visit attribution exists before attach (covers OTP/signup races).
    if (referralCode) {
      await trackContractorReferralVisit({
        referralCode,
        force: true,
        channel: "contractor_attach",
      });
    }
    await ContractorReferralApi.attachContractorRegistration({ visitorId });
  } catch {
    /* non-blocking */
  }
}

export async function trackContractorReferralVisit(options = {}) {
  const referralCode = (options.referralCode || "").trim();
  if (!referralCode) return null;

  if (!options.force && wasTrackedThisSession(referralCode)) {
    return getStoredContractorReferral();
  }

  const visitorId = getOrCreateContractorVisitorId();
  try {
    const response = await ContractorReferralApi.trackContractorReferral({
      referralCode,
      visitorId,
      landingUrl: options.landingUrl || window.location.href,
      utmSource: options.utmSource,
      utmMedium: options.utmMedium,
      utmCampaign: options.utmCampaign,
      channel: options.channel || "contractor_landing",
    });

    const data = response?.data ?? response?.Data ?? response;
    const isSuccess = response?.isSuccess ?? response?.IsSuccess ?? true;

    if (isSuccess && data) {
      storeContractorReferralFallback(
        data.referralCode || data.ReferralCode || referralCode
      );
      markTrackedThisSession(referralCode);
      return data;
    }
  } catch {
    storeContractorReferralFallback(referralCode);
    markTrackedThisSession(referralCode);
  }

  return null;
}
